import { hasProduction } from '@/helpers/customerUtils';
import {
  getInfraServiceFromFacility,
  isReadingTypeManual,
} from '@/helpers/facilityUtils';
import usePowersOfAttorney from '@/hooks/usePowersOfAttorney';
import useGroupedContractsWithExpired from '@/hooks/useGroupedContractsWithExpired';
import NotFound from '@/pages/not-found/NotFound';
import useHeatExchangeDevices from '@/pages/heat-exchange-control/hooks/useHeatExchangeDevices';
import { State } from '@/types/State';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router-dom';
import dayjs from 'dayjs';
import { Box } from 'ui/mui';
import { getSelectedCustomer } from '../../app/slices/userSlice';
import { getRoutes, notFoundRoute } from './GetRoutes';
import RouteChecker from './RouteChecker';
import RouteElement from './RouteElement';
import { setRoutes } from './routesSlice';
import { RouteType } from './types';

const RedirectWithSearch = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
};

const Routes = () => {
  const dispatch = useDispatch();
  const { hasPowersOfAttorney } = usePowersOfAttorney();
  const { selectedProperty } = useSelector((state: State) => state.user);
  const { routes } = useSelector((state: State) => state.routes);
  const selectedCustomer = useSelector((state: State) =>
    getSelectedCustomer(state),
  );
  const { availableDevices } = useHeatExchangeDevices(selectedCustomer!);

  const { groupedContractsWithExpired } = useGroupedContractsWithExpired();

  const organizationStructure = useSelector(
    (state: State) => state?.user?.organization,
  );
  const authProfile = useSelector((state: State) => state?.auth?.user?.profile);

  const hasManualDrainReading =
    selectedProperty &&
    isReadingTypeManual(getInfraServiceFromFacility(selectedProperty, 'VA'));

  const mRoutes: RouteType[] = routes
    ? routes.flatMap((route: RouteType) => {
        const parentAccessible =
          !route.hidden &&
          (!route.infraService ||
            selectedProperty.infraServices?.some(
              (service) =>
                service.name === route.infraService ||
                (route.infraService === 'ELPROD' && hasProduction(service)),
            ));

        const childRoutes = route.childRoutes
          ? route.childRoutes.filter((childRoute) => {
              const isHidden = childRoute.hidden;
              const hasRequiredService =
                !childRoute.infraService ||
                selectedProperty.infraServices?.some(
                  (service) =>
                    service.name === childRoute.infraService ||
                    (childRoute.infraService === 'ELPROD' &&
                      hasProduction(service)),
                );
              return parentAccessible && !isHidden && hasRequiredService;
            })
          : [];

        return parentAccessible ? [route, ...childRoutes] : childRoutes;
      })
    : [];

  const setRoutesInStore = () => {
    const newRoutes = getRoutes({
      selectedCustomer,
      selectedProperty,
      now: dayjs(),
      hasManualDrainReading,
      authProfile,
      organizationStructure,
      hasPowersOfAttorney,
      hasHeatExchangeDevices: !!availableDevices && availableDevices.length > 0,
      groupedContractsWithExpired,
    });
    dispatch(setRoutes(newRoutes));
  };

  useEffect(setRoutesInStore, [
    selectedCustomer,
    selectedProperty,
    hasManualDrainReading,
    authProfile,
    dispatch,
    hasPowersOfAttorney,
    availableDevices,
    groupedContractsWithExpired,
    organizationStructure,
  ]);

  return selectedCustomer && selectedProperty ? (
    <RouterRoutes>
      {mRoutes
        .filter((route) => {
          if (route.hidden) return false;
          if (route.infraService) {
            const infraService = selectedProperty.infraServices.find(
              (utility) =>
                utility.name === route.infraService ||
                (route.infraService === 'ELPROD' && hasProduction(utility)),
            );
            return !!infraService;
          }
          return true;
        })
        .map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={
              route.redirectTo ? (
                <RedirectWithSearch to={route.redirectTo} />
              ) : (
                <RouteElement route={route}>
                  <Box>
                    {RouteChecker(route, mRoutes as RouteType[], {
                      selectedProperty,
                      selectedCustomer,
                    })}
                  </Box>
                </RouteElement>
              )
            }
          />
        ))}
      <Route
        path='*'
        element={
          <RouteElement route={notFoundRoute}>
            <NotFound />
          </RouteElement>
        }
      />
    </RouterRoutes>
  ) : null;
};

export default Routes;
